import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  IconButton,
  Skeleton,
  Tooltip,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardBody,
  Chip,
  Progress,
} from '@vnetwork/ui/primitives';
import { Stat, StatGroup } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useMyWorkdays, useMyWorkdayTotal, type MyWorkday } from '@/api/account-lists';
import { useCurrentEmployee } from '@/api/account';
import { dayTypeIcon, hoursPillTone, formatWorkHours } from './workday-helpers';
import { TabRetry } from './record-parts';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const NOW = new Date();
const YEARS = Array.from({ length: 7 }, (_, i) => NOW.getFullYear() + 1 - i);
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

/** Local YYYY-MM-DD (never toISOString — that shifts UTC in Asia/Saigon). */
const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** A stat card with a title, big value and optional progress bar. */
function StatCard({
  title,
  value,
  suffix,
  progress,
}: {
  title: string;
  value: string;
  suffix?: string;
  progress?: { value: number; max: number };
}) {
  return (
    <Card flat>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
        <Stat
          label={title}
          value={
            <>
              {value}
              {suffix ? (
                <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-regular)', color: 'var(--vn-fg-muted)' }}> {suffix}</span>
              ) : null}
            </>
          }
        />
        {progress ? <Progress value={progress.value} max={progress.max} tone="primary" size="sm" /> : null}
      </CardBody>
    </Card>
  );
}

/** The current employee's monthly attendance calendar (mirrors the legacy view). */
export function WorkdayTab() {
  const [year, setYear] = useState(NOW.getFullYear());
  const [month, setMonth] = useState(NOW.getMonth() + 1); // 1-based
  const { data: emp } = useCurrentEmployee();
  const { data, isLoading, isError, refetch } = useMyWorkdays(year, month);
  const { data: totals } = useMyWorkdayTotal(year, month, emp?.employee_id);

  const rows = data?.results ?? [];
  const byDate = useMemo(() => {
    const m = new Map<string, MyWorkday>();
    for (const w of rows) m.set(w.work_day_date.slice(0, 10), w);
    return m;
  }, [rows]);

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstWeekday = new Date(year, month - 1, 1).getDay(); // 0=Sun
  const standardWorkdays = Array.from({ length: daysInMonth }, (_, i) => new Date(year, month - 1, i + 1).getDay()).filter((d) => d !== 0).length;
  const totalHours = rows.reduce((s, w) => s + (w.work_day_time || 0), 0);
  const worked = totals?.total_work_days_in_month ?? 0;
  const todayIso = iso(NOW);

  const step = (delta: number) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <StatGroup columns={2}>
        <StatCard title={`Workdays in ${month}/${year}`} value={String(worked)} suffix={`/ ${standardWorkdays}`} progress={{ value: worked, max: standardWorkdays || 1 }} />
        <StatCard title="Total actual hours" value={formatWorkHours(totalHours) || '0'} suffix="h" />
      </StatGroup>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger style={{ width: 120 }}><SelectValue /></SelectTrigger>
          <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>Year {y}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger style={{ width: 120 }}><SelectValue /></SelectTrigger>
          <SelectContent>{MONTHS.map((m) => <SelectItem key={m} value={String(m)}>Month {m}</SelectItem>)}</SelectContent>
        </Select>
        <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: 'var(--vn-space-1)' }}>
          <IconButton variant="ghost" size="sm" aria-label="Previous month" onClick={() => step(-1)}><ChevronLeft size={16} /></IconButton>
          <IconButton variant="ghost" size="sm" aria-label="Next month" onClick={() => step(1)}><ChevronRight size={16} /></IconButton>
        </div>
      </div>

      {isError ? (
        <Alert tone="danger" title="Failed to load workdays" actions={<TabRetry onRetry={refetch} />} />
      ) : isLoading ? (
        <Skeleton style={{ height: 320 }} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 'var(--vn-space-1)' }}>
            {WEEKDAYS.map((d) => (
              <div key={d} style={{ padding: 'var(--vn-space-2)', textAlign: 'center', fontWeight: 'var(--vn-font-weight-semibold)', fontSize: 'var(--vn-font-size-sm)', background: 'var(--vn-bg-muted)', borderRadius: 'var(--vn-radius-sm)' }}>{d}</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 'var(--vn-space-1)' }}>
            {Array.from({ length: firstWeekday }).map((_, i) => (
              <div key={`e${i}`} style={{ minHeight: 78, background: 'var(--vn-bg-subtle)', border: '1px solid var(--vn-border-subtle)', borderRadius: 'var(--vn-radius-sm)' }} />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const date = new Date(year, month - 1, i + 1);
              const key = iso(date);
              const w = byDate.get(key);
              const isToday = key === todayIso;
              return (
                <div
                  key={key}
                  style={{
                    minHeight: 78,
                    padding: 'var(--vn-space-2)',
                    background: w ? 'var(--vn-bg-surface)' : 'var(--vn-bg-subtle)',
                    border: '1px solid var(--vn-border-subtle)',
                    borderRadius: 'var(--vn-radius-sm)',
                    outline: isToday ? '2px solid var(--vn-primary-solid)' : undefined,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-medium)' }}>{i + 1}</span>
                    {w ? (
                      <Tooltip content={w.work_day_type ?? ''}>
                        <span style={{ display: 'inline-flex' }}>{dayTypeIcon(w.work_day_type, w.work_n_day)}</span>
                      </Tooltip>
                    ) : null}
                  </div>
                  {w && w.work_day_time ? (
                    <div style={{ marginTop: 'var(--vn-space-1)', textAlign: 'center' }}>
                      <Chip size="sm" appearance="subtle" tone={hoursPillTone(w.work_day_type, w.work_day_in, w.work_day_option)}>
                        {formatWorkHours(w.work_day_time)}
                      </Chip>
                      {w.work_day_in && w.work_day_out ? (
                        <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginTop: 'var(--vn-space-1)' }}>{w.work_day_in} · {w.work_day_out}</div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
